import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable , throwError } from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import { RestUrlsConfig, GenericConfig } from "@signature-it/ngx-generic/config";
import { SolrRequestDTO } from "@signature-it/ngx-generic/models";
import { ILanguage } from "@signature-it/ngx-generic/interfaces";
import { LanguageService } from "@signature-it/ngx-generic/services";
import { ProductSolrDTO, ProductFullSolrDTO, SolrConditionDTO } from "@signature-it/ngx-catalogue/models";
import { SolrService } from './solr.service';
import { GroupApiService } from './group.service';
import { SharedDataService } from './shared-data.service';
import { Router } from "@angular/router";
import * as _ from 'underscore';

@Injectable({
  providedIn: 'root'
})
export class SolrProductService extends SolrService {

    constructor(
        protected http: HttpClient,
        protected config: RestUrlsConfig,
        protected router: Router,
        protected groupApiSvc: GroupApiService,
        protected sharedSvc: SharedDataService,
        protected langSvc: LanguageService
    ) {
        super(http, config, router, groupApiSvc, sharedSvc, langSvc )
    }

    getSolrByProductId(lang: ILanguage, productId:number, extraQryString = '', taxonomyVar = '', leafNodeUri = ''): Observable<ProductFullSolrDTO> {
        return this.http.get<ProductFullSolrDTO>(this.getSolrUrl+`?fq[]=lang_code_s:${lang.code}&fq[]=product_id_i:${productId}${extraQryString}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const docs = response.docs || [];
                return new ProductFullSolrDTO({...docs[0], ...{taxonomy: taxonomyVar, leafNodeUri: leafNodeUri}});
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByProductIds(lang: ILanguage, productIds:Array<number>, isUseSolrParameter = false): Observable<ProductFullSolrDTO[]> {
        const rows = productIds?.length;
        let url = this.getSolrUrl+`?rows=${rows}&fq[]=lang_code_s:${lang.code}&fq[]=product_id_i:(${productIds.join(' OR ')})`;
        let params = [];
        if(isUseSolrParameter) {
            if (GenericConfig.solr_parameters) {
                _.forEach(GenericConfig.solr_parameters, function(val, key) {
                    if (_.isArray(val)) {
                        _.forEach(val, function(v, k) {
                            params.push(v + '=' + k);
                        });
                    } else{
                        params.push(key + '=' + val);
                    }
                });
                url = this.getSolrUrl+`?rows=${rows}&fq[]=lang_code_s:${lang.code}&fq[]=product_id_i:(${productIds.join(' OR ')})&${params.join('&')}`;
            }
        }
        return this.http.get<ProductFullSolrDTO[]>(url)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrIsPromo(lang: ILanguage, conditions:Array<SolrConditionDTO>): Observable<ProductFullSolrDTO[]> {
        const request = new SolrRequestDTO();
        const currLang = this.langSvc.getCurrentLanguage();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);

        conditions.forEach(c => {
            request.addQueryParameter(c.key, `${c.value}`);
        });

        return this.http.get<ProductFullSolrDTO[]>(this.getSolrUrl+`?${request.paramsToString()}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrIsPromoFullData(lang: ILanguage, conditions:Array<SolrConditionDTO>,extraQryString = '',isLang= true, getSolrUrl = ''): Observable<ProductFullSolrDTO[]> {
        const request = new SolrRequestDTO();
        const currLang = this.langSvc.getCurrentLanguage();
        if(isLang) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        }
        if(extraQryString != '') {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, extraQryString );
        }
        conditions.forEach(c => {
            request.addQueryParameter(c.key, `${c.value}`);
        });

        return this.http.get<ProductFullSolrDTO[]>((getSolrUrl || this.getSolrUrl)+`?${request.paramsToString()}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                return response;
            }), catchError((error: any) => throwError(error)));
    }

    getContext(group:number, contextId:number, taxonomyVar = null, leafNodeUri = null, catalog_group_id = null): Observable<any> {
        const currLang = this.langSvc.getCurrentLanguage();
        const taxonomyVarParam = taxonomyVar? `&taxonomyVar=${taxonomyVar}`: '';
        const leafNodeUriParam = leafNodeUri? `&leafNodeUri=${leafNodeUri}`: '';
        const catalogGroupIdParam = catalog_group_id? `&catalogGroupIdParam=${catalog_group_id}`: '';
        return this.http.get(`${this.config.appURL}/public/group/${group.toString()}/fieldLinks.json?context_id=${contextId}&lang=${currLang.code}${taxonomyVarParam}${leafNodeUriParam}${catalogGroupIdParam}`)
            .pipe(tap((res) => res, (error: any) => throwError(error)));
    }

    getSolrRelatedItemsByProductId(lang: ILanguage, productId:number) {
        return this.http.get(this.getSolrUrl+`?fq[]=lang_code_s:${lang.code}&fq[]=cp_kits_for_is:(${productId})`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrProductsInFamilyByFamilyName(lang: ILanguage, groupFamilyName: string) {
        const url = this.getSolrUrl + `?fq[]=lang_code_s:${lang.code}&fq[]=GroupFamilyName_s:"${this.escapeHash(this.escapeLucene(groupFamilyName))}"&facet=true&facet.field=GroupFamilyName_s&facet.limit=1&rows=0`;
        return this.http.get(url)
            .pipe(map((res) => {
                const facet_counts = res['facet_counts'] || {};
                if (facet_counts['facet_fields']) {
                    return facet_counts['facet_fields']['GroupFamilyName_s'][0][1];
                }
                return -1;
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByProductCode(productCode: string, lang: ILanguage|null = null, taxonomyVar = '', leafNodeUri = ''): Observable<ProductFullSolrDTO> {
		const langFq = lang? `&fq[]=lang_code_s:${lang.code}`: '';
        return this.http.get<ProductFullSolrDTO>(this.getSolrUrl+`?rows=1&fq[]=product_code_s:${productCode}${langFq}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const docs = response.docs || [];
                return new ProductFullSolrDTO({...docs[0], ...{taxonomy: taxonomyVar, leafNodeUri: leafNodeUri}});
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByProductCodeAndFacets(productCode: string, lang: ILanguage|null = null, rows: number = 1, facets: string = ''): Observable<{ }> {
		const langFq = lang? `&fq[]=lang_code_s:${lang.code}`: '';
        return this.http.get<ProductFullSolrDTO>(this.getSolrUrl+`?rows=${rows}&fq[]=product_code_s:"${productCode}"${langFq}${facets}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const docs = response.docs || [];
                if (rows == 1) return new ProductFullSolrDTO(docs[0] || {});
                const facet_counts = res['facet_counts'] || {};
                return ({docs: docs, facet_counts: facet_counts});
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByProductCodes(productCodes: Array<string>, lang: ILanguage|null = null):  Observable<ProductFullSolrDTO[]> {
        const rows = productCodes.length;
        return this.http.get<ProductFullSolrDTO[]>(this.getSolrUrl+`?rows=${rows}&fq[]=lang_code_s:${lang.code}&fq[]=product_code_s:(${productCodes.join(' OR ')})`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByScannedBarcode(code): Observable<ProductSolrDTO> {
        const fields = GenericConfig.scannerProductFields || ['sku'];
        const url = `${this.getSolrUrl}?rows=1&fq[]=(${fields.map(f => `${f}:"${code}" OR`).join(' ').slice(0, -3)})`;
        return this.http.get<ProductSolrDTO>(url)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const docs = response.docs || [];
                return ProductSolrDTO.create(docs[0]);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByProductIdsAndConditions(lang: ILanguage, productIds:Array<number>, conditions:Array<SolrConditionDTO>): Observable<ProductFullSolrDTO[]> {
        const request = new SolrRequestDTO();
        const currLang = this.langSvc.getCurrentLanguage();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `product_id_i:(${productIds.join(' OR ')})`);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_ROWS, productIds.length);

        conditions.forEach(c => {
            request.addQueryParameter(c.key, `${c.value}`);
        });

        return this.http.get<ProductFullSolrDTO[]>(this.getSolrUrl+`?${request.paramsToString()}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }

    getSolrByConditions(condition, excludeProduct, currLang: ILanguage, page, count = 12) : Observable<ProductFullSolrDTO[]>{
        const request = new SolrRequestDTO();
        request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `lang_code_s:${currLang.code}`);
        if(excludeProduct) {
            request.addQueryParameter(SolrRequestDTO.PARAMETER_FQ, `-product_id_i:${excludeProduct}`);
        }


        request.addQueryParameter(SolrRequestDTO.PARAMETER_ROWS, count);
        request.addQueryParameter(SolrRequestDTO.PARAMETER_START, ((page - 1) * count).toString());

        return this.http.get<ProductFullSolrDTO[]>(this.getSolrUrl+`?${request.paramsToString()}&${condition}`)
            .pipe(map((res) => {
                const response = res['response'] || {};
                const data = response.docs || [];
                return ProductFullSolrDTO.createFromArray(data);
            }), catchError((error: any) => throwError(error)));
    }
}
